import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlined"
import EditIcon from "@mui/icons-material/Edit"
import { Divider, Tooltip, Typography } from "@mui/material"
import type { BiomeshSensorResponse } from "@phc/common/gen/threatAnalyticsGen"
import { dayjsUTC } from "../../../utils/dates"
import { extraColors } from "../../../utils/theme"
import {
  operationalStatusChipColors,
  operationalStatusLabels,
} from "../../Cop/copUtils"
import { Tag } from "../../Shared/GenericTag"
import { SensorEventsSection } from "./SensorEventsSection"
import {
  ActionButton,
  CardHeader,
  CardRoot,
  CardTitle,
  Row,
  RowGroup,
  SectionLabel,
} from "./shared"

export function SensorCard({
  sensor,
  onEdit,
  onDelete,
}: {
  sensor: BiomeshSensorResponse
  onEdit?: () => void
  onDelete?: () => void
}) {
  const opColor = operationalStatusChipColors[sensor.operational_status]

  return (
    <CardRoot>
      <CardHeader sx={{ mb: 2 }}>
        <CardTitle variant="body2Bold">{sensor.sensor_name}</CardTitle>
        {onEdit && (
          <Tooltip title="Edit sensor">
            <ActionButton
              size="small"
              onClick={onEdit}
              aria-label="Edit sensor"
            >
              <EditIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        )}
        {onDelete && (
          <Tooltip title="Delete sensor">
            <ActionButton
              size="small"
              onClick={onDelete}
              aria-label="Delete sensor"
            >
              <DeleteOutlineIcon fontSize="small" />
            </ActionButton>
          </Tooltip>
        )}
      </CardHeader>

      <SectionLabel variant="small1Bold">Readiness</SectionLabel>

      <RowGroup>
        <Row
          label="Operational Status"
          value={
            <Tag
              sx={{
                bgcolor: opColor.bg,
                color: opColor.fg,
                borderColor: opColor.border,
              }}
            >
              <Typography variant="small1Bold" color="inherit">
                {operationalStatusLabels[sensor.operational_status]}
              </Typography>
            </Tag>
          }
        />
        <Row
          label="Last Sample Collected"
          value={dayjsUTC(sensor.last_sample_collected_at).fromNow()}
        />
        <Row
          label="Last Result Reported"
          value={dayjsUTC(sensor.last_sample_result_reported_at).fromNow()}
        />
      </RowGroup>

      {(sensor.sensor_make_model || sensor.sensor_notes) && (
        <>
          <Divider sx={{ my: 2 }} />
          <SectionLabel variant="small1Bold">Details</SectionLabel>

          <RowGroup>
            {sensor.sensor_make_model && (
              <Row label="Make / Model" value={sensor.sensor_make_model} />
            )}
          </RowGroup>
          {sensor.sensor_notes && (
            <Typography
              variant="small1"
              sx={{ color: extraColors.medium, mt: 0.5 }}
            >
              {sensor.sensor_notes}
            </Typography>
          )}
        </>
      )}

      <Divider sx={{ my: 2 }} />

      <SensorEventsSection sensorId={sensor.sensor_id} />
    </CardRoot>
  )
}
